import {
    getFirestore, collection, query, getDocs, orderBy,
    addDoc, deleteDoc, updateDoc, doc, serverTimestamp, getDoc
} from "firebase/firestore";
import { app } from "../firebase-config.js";
import { uploadImage } from "./storage-service.js"; // Reuse existing
import { locale } from "./LocaleService.js";
// Note: Image compression usually happens client-side (in UI) to save bandwidth before Service call, 
// or Service can handle it if passed a file. We will assume pre-compressed blob or handle compression in UI.

const db = getFirestore(app);

export const AdminPopupService = {
    // --- Popups ---

    getPopups: async () => {
        const q = query(collection(db, 'popups'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * Creates a popup and optionally uploads an image
     * @param {Object} popupData - { title, startDate, endDate, linkUrl, ... }
     * @param {Blob} imageBlob - Compressed Image Blob
     * @param {string} filename - Original filename for reference
     */
    createPopup: async (popupData, imageBlob, filename) => {
        try {
            // Upload Image
            const storagePath = `popups/${Date.now()}_${filename.split('.')[0]}.webp`;
            const imageUrl = await uploadImage(imageBlob, storagePath);

            // Create Doc
            const docRef = await addDoc(collection(db, 'popups'), {
                ...popupData,
                imageUrl,
                isActive: true, // Default active
                createdAt: serverTimestamp()
            });

            return { id: docRef.id, imageUrl, ...popupData };
        } catch (e) {
            console.error("Create Popup Error:", e);
            throw e;
        }
    },

    deletePopup: async (id) => {
        await deleteDoc(doc(db, 'popups', id));
    },

    updatePopup: async (id, data) => {
        await updateDoc(doc(db, 'popups', id), data);
    },

    // --- Notices ---

    getNotices: async () => {
        const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    createNotice: async (noticeData) => {
        const docRef = await addDoc(collection(db, 'notices'), {
            ...noticeData,
            createdAt: serverTimestamp()
        });
        return docRef.id;
    },

    updateNotice: async (id, data) => {
        await updateDoc(doc(db, 'notices', id), data);
    },

    deleteNotice: async (id) => {
        await deleteDoc(doc(db, 'notices', id));
    },

    // --- Copyright Police Factory ---

    getCopyrightSettings: async () => {
        const snap = await getDoc(doc(db, 'settings', 'copyright_police'));
        if (snap.exists()) return snap.data();
        // Default Config (Fallback)
        return {
            mode: 'default', // 'default' | 'custom'
            title: "꼼짝 마! 저작권 경찰!",
            legalText: "저작권법 제136조 위반 경고",
            buttonText: "네, 착하게 감상할게요!",
            theme: 'standard', // 'standard' | 'police' | 'romance' | 'horror'
        };
    },

    // --- Globalization Helper (Centralized) ---

    /**
     * Resolve Browser Language to Supported Language Code (9 Langs)
     * e.g., 'en-US' -> 'en', 'ja-JP' -> 'ja', 'ko-KR' -> 'ko'
     * Fallback: 'ko'
     */
    resolveLanguage: (navigatorLang) => {
        if (!navigatorLang) return 'ko';
        const langCode = navigatorLang.split('-')[0].toLowerCase();
        const supported = ['ko', 'en', 'ja', 'zh', 'es', 'fr', 'de', 'vi', 'th', 'ru'];
        // console.log(`[AdminPopupService] Language Resolve: ${navigatorLang} -> ${langCode}`); 
        return supported.includes(langCode) ? langCode : 'ko';
    },

    /**
     * Get Copyright Settings with Auto-Language Resolution
     * This replaces the fragmentation in viewers.
     */
    getResolvedCopyrightSettings: async (forcedLang = null) => {
        const settings = await AdminPopupService.getCopyrightSettings();

        // [FIX v1.36.245] Priority: Forced -> App Locale (LocaleService) -> System (Navigator)
        // This ensures that if user switches language in App, the popup follows suit.
        let rawLang = forcedLang;
        if (!rawLang && locale && locale.lang) {
            rawLang = locale.lang;
        }
        if (!rawLang) rawLang = navigator.language;

        const userLang = AdminPopupService.resolveLanguage(rawLang);

        // Debug Log
        console.log(`[Copyright] Loading Settings. UserLang: ${userLang} (Raw: ${rawLang}), HasTranslations: ${!!settings.translations}`);
        if (settings.translations) {
            console.log(`[Copyright] Available Keys: ${Object.keys(settings.translations).join(', ')}`);
        }

        // If user is Korean or no translations exist, return as is (Default)
        if (userLang === 'ko' || !settings.translations || !settings.translations[userLang]) {
            console.log(`[Copyright] Fallback to Korean/Default (Lang: ${userLang})`);
            return settings;
        }

        // Merge Translation
        const tr = settings.translations[userLang];
        console.log(`[Copyright] Merging Translation for ${userLang}:`, tr);

        return {
            ...settings,
            title: tr.title || settings.title,
            legalText: tr.legalText || settings.legalText,
            buttonText: tr.buttonText || settings.buttonText
        };
    },



    /**
     * Resolve Translation for Generic Data (e.g., Notice)
     */
    resolveTranslation: (data, forcedLang = null) => {
        if (!data) return data;

        // [FIX v1.36.248] Direct Dependency on LocaleService (No Global)
        let rawLang = forcedLang;
        if (!rawLang && locale && locale.lang) {
            rawLang = locale.lang;
        }
        if (!rawLang) rawLang = navigator.language;

        const userLang = AdminPopupService.resolveLanguage(rawLang);

        // Debug Log
        console.log(`[Notice] Resolve. UserLang: ${userLang}, RawLang: ${rawLang}, Keys: ${data.translations ? Object.keys(data.translations) : 'None'}`);

        if (userLang === 'ko' || !data.translations || !data.translations[userLang]) {
            return data;
        }

        const tr = data.translations[userLang];
        return {
            ...data,
            title: tr.title || data.title,
            content: tr.content || data.content,
            imageUrl: tr.imageUrl || data.imageUrl,
            linkUrl: tr.linkUrl || data.linkUrl
        };
    },

    updateCopyrightSettings: async (settings) => {
        await updateDoc(doc(db, 'settings', 'copyright_police'), {
            ...settings,
            updatedAt: serverTimestamp()
        }).catch(async (e) => {
            // If doc doesn't exist, create it (setDoc)
            if (e.code === 'not-found') {
                const { setDoc } = await import("firebase/firestore");
                await setDoc(doc(db, 'settings', 'copyright_police'), {
                    ...settings,
                    updatedAt: serverTimestamp()
                });
            } else {
                throw e;
            }
        });
    }
};
